/**
 * Drives the "Need a different size?" field on product pages.
 *
 * Two jobs:
 *
 * 1. Reveal the free-text note for choices that need one ("Custom length",
 *    "Other"). The `required` toggle matters: a required field that is hidden
 *    cannot be focused, and the browser then refuses to submit the form while
 *    reporting nothing — Add to cart just appears dead.
 *
 * 2. Track whether the SELECTED variant has stock, and require a size when it
 *    doesn't. This has to happen client-side: the Section Rendering API is
 *    disabled on this store, so switching variants never re-renders the block.
 *    Without it a shopper could switch to a zero-stock variant and back-order
 *    it with no size attached — the exact thing the field exists to prevent.
 *
 * Variant `available` is useless for this: once variants are set to keep
 * selling past zero, everything reports available. So the per-variant stock
 * counts are embedded server-side and looked up here.
 */
class CustomSizeField extends HTMLElement {
  #initialized = false;

  connectedCallback() {
    if (this.#initialized) return;

    // On products that sell size as a variant there is no dropdown — the block
    // is only the lead-time flag there, so everything below has to cope with a
    // missing select rather than bailing out.
    this.select = this.querySelector('[data-size-select]');

    this.otherField = this.querySelector('[data-size-other]');
    this.otherInput = this.otherField?.querySelector('input');
    this.details = this.querySelector('[data-size-disclosure]');
    this.requiredMark = this.querySelector('[data-required-mark]');
    this.leadTime = this.querySelector('[data-leadtime]');
    this.resetButton = this.querySelector('[data-size-reset]');
    this.requireWhenSoldOut = this.dataset.requireWhenSoldOut === 'true';
    this.inventory = this.#readInventory();

    this.select?.addEventListener('change', this.#handleChange);
    this.resetButton?.addEventListener('click', this.#handleReset);
    this.details?.addEventListener('toggle', this.#guardCollapse);

    // The theme dispatches variant:update on the containing section.
    this.section = this.closest('.shopify-section') ?? document;
    this.section.addEventListener('variant:update', this.#handleVariantUpdate);

    this.#initialized = true;
    this.#handleChange();
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.#handleChange);
    this.resetButton?.removeEventListener('click', this.#handleReset);
    this.details?.removeEventListener('toggle', this.#guardCollapse);
    this.section?.removeEventListener('variant:update', this.#handleVariantUpdate);
    this.#initialized = false;
  }

  /**
   * Back out of a custom size and take the on-hand variant instead.
   *
   * Selects the placeholder, which is a disabled option — the form entry list
   * skips disabled options, so no empty Size property is attached to the order.
   */
  #handleReset = () => {
    if (!this.select) return;
    this.select.value = '';
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #readInventory() {
    try {
      return JSON.parse(this.querySelector('[data-size-inventory]')?.textContent || '{}');
    } catch {
      return {};
    }
  }

  #handleVariantUpdate = (event) => {
    const id = String(event.detail?.resource?.id ?? '');
    const info = this.inventory[id];
    if (!info) return;

    // Untracked variants are always considered in stock.
    this.#applySoldOut(Boolean(info.tracked) && Number(info.qty) <= 0);
  };

  /** Require (and force open) when the chosen variant has nothing on hand. */
  #applySoldOut(soldOut) {
    const required = soldOut && this.requireWhenSoldOut && Boolean(this.select);

    if (this.leadTime) this.leadTime.hidden = !soldOut;
    if (!this.select) return;

    this.select.required = required;
    this.select.setAttribute('aria-required', String(required));

    if (this.requiredMark) this.requiredMark.hidden = !required;

    this.details?.classList.toggle('custom-size__disclosure--locked', required);
    if (required && this.details) this.details.open = true;

    this.#syncReset();
  }

  /** Reset only makes sense with a size chosen and no requirement to keep one. */
  #syncReset() {
    if (!this.resetButton || !this.select) return;
    this.resetButton.hidden = !this.select.value || this.select.required;
  }

  /** A required control inside a collapsed <details> can't be focused. */
  #guardCollapse = () => {
    if (this.select?.required && this.details && !this.details.open) {
      this.details.open = true;
    }
  };

  #handleChange = () => {
    this.#syncReset();

    if (!this.otherField || !this.select) return;

    const isOther = this.select.selectedOptions[0]?.dataset.other === 'true';

    this.otherField.hidden = !isOther;

    if (!this.otherInput) return;
    this.otherInput.required = isOther;

    // Hidden inputs still submit, which would attach an empty note property to
    // every order. Disabled ones are left out of the form's entry list.
    this.otherInput.disabled = !isOther;

    if (isOther) this.otherInput.focus();
    else this.otherInput.value = '';
  };
}

if (!customElements.get('custom-size-field')) {
  customElements.define('custom-size-field', CustomSizeField);
}
